/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetLatestDeploymentRequest,
  GetLatestDeploymentSecurity,
} from "../models/operations/getlatestdeployment.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildLatestDeploymentQuery,
  LatestDeploymentQueryData,
  prefetchLatestDeployment,
  queryKeyLatestDeployment,
} from "./latestDeployment.core.js";
export {
  buildLatestDeploymentQuery,
  type LatestDeploymentQueryData,
  prefetchLatestDeployment,
  queryKeyLatestDeployment,
};

export type LatestDeploymentQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getLatestDeployment deployments
 *
 * @remarks
 * Get the latest deployment for a project.
 */
export function useLatestDeployment(
  request?: GetLatestDeploymentRequest | undefined,
  security?: GetLatestDeploymentSecurity | undefined,
  options?: QueryHookOptions<
    LatestDeploymentQueryData,
    LatestDeploymentQueryError
  >,
): UseQueryResult<LatestDeploymentQueryData, LatestDeploymentQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildLatestDeploymentQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getLatestDeployment deployments
 *
 * @remarks
 * Get the latest deployment for a project.
 */
export function useLatestDeploymentSuspense(
  request?: GetLatestDeploymentRequest | undefined,
  security?: GetLatestDeploymentSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    LatestDeploymentQueryData,
    LatestDeploymentQueryError
  >,
): UseSuspenseQueryResult<
  LatestDeploymentQueryData,
  LatestDeploymentQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildLatestDeploymentQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setLatestDeploymentData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: LatestDeploymentQueryData,
): LatestDeploymentQueryData | undefined {
  const key = queryKeyLatestDeployment(...queryKeyBase);

  return client.setQueryData<LatestDeploymentQueryData>(key, data);
}

export function invalidateLatestDeployment(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "deployments", "latest", ...queryKeyBase],
  });
}

export function invalidateAllLatestDeployment(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "deployments", "latest"],
  });
}
